/**
 * Compact "Bevorzugte Dienstleister" section on the property page (EHV-1302).
 *
 * Staff pick, per provider type (Sanitär, Elektriker, Hausmeister, ...), the
 * contacts the property prefers. The list lives in Lux
 * (`PropertyPreferredProviders`) and is loaded/saved through
 * `/api/impowerX/properties/{propertyId}/preferred-providers` with the NextAuth
 * session cookie (`credentials: 'include'`). Contact search and names come from
 * Impower with the Impower bearer token, like `contactRolesField.js`.
 *
 * The list is edited locally and persisted with its own "Speichern" button
 * (replace semantics); editing is only possible while the property is in edit
 * mode (`shouldDisableInjectedFields`).
 *
 * Called from onPropertyPageMatch.js. Gulp concatenates src/**\/*.js into one
 * IIFE (features/ before pageInjects/), so this top-level function declaration
 * is hoisted and visible at the call site.
 */
const LUX_PP_STYLE_ID = 'lux-preferred-providers-styles'

function luxPpEsc(value = '') {
  return typeof escapeHtml === 'function'
    ? escapeHtml(value)
    : String(value).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`)
}

function luxPpFormatContactName(contact) {
  if (contact.companyName) return contact.companyName
  const parts = [contact.firstName, contact.lastName].filter(Boolean)
  return parts.join(' ') || contact.name || `Kontakt ${contact.id}`
}

function luxPpIdentity(entry) {
  return `${entry.contactId}|${entry.providerTypeKey}|${entry.caseTypeKey || ''}`
}

function luxPpImpowerHeaders() {
  const token = JSON.parse(localStorage.getItem('auth') || '{}').token
  return token
    ? { Authorization: `Bearer ${token}`, Accept: 'application/json' }
    : { Accept: 'application/json' }
}

async function luxPpFetchImpowerJson(url) {
  try {
    const response = await fetch(url, { headers: luxPpImpowerHeaders() })
    return response.ok ? await response.json() : null
  } catch {
    return null
  }
}

function luxPpContactSearchRows(data) {
  if (Array.isArray(data?.content)) return data.content
  if (Array.isArray(data)) return data
  return []
}

async function luxPpSearchContacts(query, contactNames) {
  const params = new URLSearchParams({ size: '10', name: query })
  const data = await luxPpFetchImpowerJson(
    `/services/pmp-accounting/api/v1/contacts/search?${params.toString()}&types=PERSON,COMPANY,MANAGEMENT_COMPANY`
  )
  const rows = luxPpContactSearchRows(data)
  return rows
    .map((contact) => {
      const id = Number(contact?.id)
      if (!Number.isInteger(id) || id <= 0) return null
      const name = luxPpFormatContactName(contact)
      contactNames.set(id, name)
      return { id, name }
    })
    .filter(Boolean)
}

async function luxPpResolveContactNames(entries, contactNames) {
  const missing = [...new Set(entries.map((entry) => entry.contactId))].filter(
    (id) => !contactNames.has(id)
  )
  await Promise.all(
    missing.map(async (id) => {
      const detail = await luxPpFetchImpowerJson(
        `/services/pmp-accounting/api/v1/contacts/${encodeURIComponent(String(id))}`
      )
      contactNames.set(
        id,
        detail ? luxPpFormatContactName(detail) : `Kontakt ${id}`
      )
    })
  )
}

async function luxPpRequestProviders(providersUrl, options = {}) {
  const response = await fetch(providersUrl, {
    credentials: 'include',
    ...options,
    headers: {
      Accept: 'application/json',
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    },
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok || !Array.isArray(data?.providers)) {
    throw new Error(data?.error || `HTTP ${response.status}`)
  }
  const providers = data.providers.map((provider) => ({
    contactId: Number(provider.contactId),
    providerTypeKey: provider.providerTypeKey,
    caseTypeKey: provider.caseTypeKey || null,
  }))
  return {
    providers,
    providerTypes: Array.isArray(data.providerTypes)
      ? data.providerTypes
      : null,
  }
}

function luxPpInjectStyles() {
  if (document.getElementById(LUX_PP_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = LUX_PP_STYLE_ID
  style.textContent = `
      .lux-pp { margin: 16px 0; }
      .lux-pp-title { font-weight: 600; margin-bottom: 8px; }
      .lux-pp-empty { color: rgba(0,0,0,.45); font-size: 13px; }
      .lux-pp-row { display: flex; align-items: center; gap: 12px; padding: 6px 0; border-bottom: 1px solid #f0f0f0; }
      .lux-pp-type { width: 220px; color: rgba(0,0,0,.65); }
      .lux-pp-name { flex: 1; }
      .lux-pp-remove { border: 0; background: none; color: #d4380d; cursor: pointer; }
      .lux-pp-add { display: flex; gap: 12px; margin-top: 12px; position: relative; flex-wrap: wrap; }
      .lux-pp-add select, .lux-pp-add input { height: 32px; border: 1px solid #d9d9d9; border-radius: 4px; padding: 0 8px; }
      .lux-pp-add select { width: 220px; }
      .lux-pp-add input { width: 280px; }
      .lux-pp-results { position: absolute; top: 36px; left: 232px; width: 280px; background: #fff; border: 1px solid #d9d9d9; border-radius: 4px; box-shadow: 0 4px 12px rgba(0,0,0,.12); z-index: 2000; max-height: 240px; overflow-y: auto; }
      .lux-pp-result { padding: 6px 10px; cursor: pointer; }
      .lux-pp-result:hover { background: #f5f5f5; }
      .lux-pp-result-empty { padding: 6px 10px; color: rgba(0,0,0,.45); }
      .lux-pp-save { margin-top: 12px; height: 32px; padding: 0 16px; border: 0; border-radius: 4px; background: #1d3f73; color: #fff; cursor: pointer; }
      .lux-pp-save:disabled { background: #d9d9d9; cursor: not-allowed; }
    `
  document.head.appendChild(style)
}

function luxPpTypeLabel(providerTypes, key) {
  return providerTypes.find((type) => type.key === key)?.label || key
}

function luxPpTypeOptionsHtml(providerTypes) {
  return providerTypes
    .map(
      (type) =>
        `<option value="${luxPpEsc(type.key)}">${luxPpEsc(type.label)}</option>`
    )
    .join('')
}

function luxPpIsDirty(ctx) {
  return (
    ctx.state.map(luxPpIdentity).join(',') !==
    ctx.saved.map(luxPpIdentity).join(',')
  )
}

function luxPpIsDisabled(ctx) {
  return typeof ctx.shouldDisableInjectedFields === 'function'
    ? ctx.shouldDisableInjectedFields()
    : false
}

function luxPpNotify(message, type = 'error') {
  if (typeof showToast === 'function') showToast(message, type)
  else console.warn(message)
}

function luxPpCloseResults(hostElement) {
  hostElement.querySelector('.lux-pp-results')?.remove()
}

function luxPpRender(ctx) {
  if (ctx.disposed) return
  // Never paint the editor from empty state before a successful load —
  // that looks like "nothing stored" and a Speichern would wipe real data.
  if (!ctx.loaded) {
    if (ctx.hostElement.dataset.luxPpLoad === 'failed') {
      ctx.hostElement.innerHTML = `
      <div class="lux-pp">
        <div class="lux-pp-title">Bevorzugte Dienstleister</div>
        <div class="lux-pp-empty">Die bevorzugten Dienstleister konnten nicht geladen werden.</div>
      </div>`
    }
    return
  }
  const disabled = luxPpIsDisabled(ctx)
  const rows = ctx.state.length
    ? ctx.state
        .map(
          (entry, index) => `
          <div class="lux-pp-row">
            <span class="lux-pp-type">${luxPpEsc(luxPpTypeLabel(ctx.providerTypes, entry.providerTypeKey))}</span>
            <span class="lux-pp-name">${luxPpEsc(ctx.contactNames.get(entry.contactId) || `Kontakt ${entry.contactId}`)}</span>
            ${disabled ? '' : `<button type="button" class="lux-pp-remove" data-index="${index}">Entfernen</button>`}
          </div>`
        )
        .join('')
    : '<div class="lux-pp-empty">Keine bevorzugten Dienstleister hinterlegt.</div>'

  ctx.hostElement.innerHTML = `
      <div class="lux-pp">
        <div class="lux-pp-title">Bevorzugte Dienstleister</div>
        ${rows}
        ${
          disabled
            ? ''
            : `<div class="lux-pp-add">
          <select class="lux-pp-add-type">${luxPpTypeOptionsHtml(ctx.providerTypes)}</select>
          <input type="text" class="lux-pp-add-search" placeholder="Kontakt suchen und hinzufügen" autocomplete="off" />
        </div>
        <button type="button" class="lux-pp-save" ${luxPpIsDirty(ctx) && !ctx.isSaving ? '' : 'disabled'}>Speichern</button>`
        }
      </div>`
}

function luxPpAddEntry(ctx, contact, providerTypeKey) {
  const entry = { contactId: contact.id, providerTypeKey, caseTypeKey: null }
  if (
    ctx.state.some(
      (existing) => luxPpIdentity(existing) === luxPpIdentity(entry)
    )
  ) {
    luxPpNotify(
      'Dieser Dienstleister ist für den Typ bereits hinterlegt.',
      'warning'
    )
    return
  }
  ctx.contactNames.set(contact.id, contact.name)
  ctx.state = [...ctx.state, entry]
  luxPpRender(ctx)
}

function luxPpShowResults(ctx, contacts, providerTypeKey) {
  luxPpCloseResults(ctx.hostElement)
  const list = document.createElement('div')
  list.className = 'lux-pp-results'
  if (!contacts.length) {
    list.innerHTML =
      '<div class="lux-pp-result-empty">Keine Kontakte gefunden</div>'
  } else {
    contacts.forEach((contact) => {
      const item = document.createElement('div')
      item.className = 'lux-pp-result'
      item.textContent = contact.name
      item.addEventListener('mousedown', (event) => {
        event.preventDefault()
        luxPpAddEntry(ctx, contact, providerTypeKey)
      })
      list.appendChild(item)
    })
  }
  ctx.hostElement.querySelector('.lux-pp-add')?.appendChild(list)
}

async function luxPpSave(ctx) {
  if (!ctx.loaded || ctx.isSaving || !luxPpIsDirty(ctx)) return
  ctx.isSaving = true
  luxPpRender(ctx)
  try {
    const { providers, providerTypes } = await luxPpRequestProviders(
      ctx.providersUrl,
      {
        method: 'PUT',
        body: JSON.stringify({ providers: ctx.state }),
      }
    )
    if (providerTypes) ctx.providerTypes = providerTypes
    ctx.saved = providers
    ctx.state = [...ctx.saved]
    luxPpNotify('Bevorzugte Dienstleister gespeichert.', 'success')
  } catch (error) {
    console.error(
      '[ImpowerX] Bevorzugte Dienstleister nicht gespeichert:',
      error
    )
    luxPpNotify(
      'Die bevorzugten Dienstleister konnten nicht gespeichert werden.'
    )
  } finally {
    ctx.isSaving = false
    luxPpRender(ctx)
  }
}

function luxPpOnClick(ctx, event) {
  if (!ctx.loaded) return
  const remove = event.target.closest('.lux-pp-remove')
  if (remove) {
    ctx.state = ctx.state.filter((_, i) => i !== Number(remove.dataset.index))
    luxPpRender(ctx)
  } else if (event.target.closest('.lux-pp-save')) {
    luxPpSave(ctx)
  }
}

function luxPpOnInput(ctx, event) {
  if (!event.target.classList.contains('lux-pp-add-search')) return
  clearTimeout(ctx.searchTimer)
  const query = event.target.value.trim()
  // Bump searchSeq so an in-flight search for a longer query cannot reopen
  // results after the field is already shorter than the minimum.
  if (query.length < 2) {
    ctx.searchSeq++
    return luxPpCloseResults(ctx.hostElement)
  }
  const seq = ++ctx.searchSeq
  ctx.searchTimer = setTimeout(async () => {
    const contacts = await luxPpSearchContacts(query, ctx.contactNames)
    if (ctx.disposed || seq !== ctx.searchSeq) return
    const typeKey = ctx.hostElement.querySelector('.lux-pp-add-type')?.value
    if (typeKey) luxPpShowResults(ctx, contacts, typeKey)
  }, 250)
}

function luxPpOnFocusOut(ctx, event) {
  if (event.target.classList.contains('lux-pp-add-search')) {
    setTimeout(() => luxPpCloseResults(ctx.hostElement), 150)
  }
}

async function luxPpLoadInitial(ctx) {
  try {
    const { providers, providerTypes } = await luxPpRequestProviders(
      ctx.providersUrl
    )
    if (ctx.disposed) return
    if (providerTypes) ctx.providerTypes = providerTypes
    ctx.saved = providers
    ctx.state = [...ctx.saved]
    await luxPpResolveContactNames(ctx.saved, ctx.contactNames)
    if (ctx.disposed) return
    ctx.loaded = true
    ctx.hostElement.dataset.luxPpLoad = 'ok'
    luxPpRender(ctx)
  } catch (error) {
    console.error(
      '[ImpowerX] Bevorzugte Dienstleister konnten nicht geladen werden:',
      error
    )
    // Keep the host in the DOM. Removing it re-triggers the property-page
    // MutationObserver, which would recreate the section and refetch forever.
    // Show a non-editable error (not an empty editor) so mode flips cannot
    // paint "Keine … hinterlegt" over unloaded state and invite a wipe-save.
    if (!ctx.disposed) {
      ctx.hostElement.dataset.luxPpLoad = 'failed'
      luxPpRender(ctx)
    }
  }
}

function initPreferredProvidersPropertySection(
  hostElement,
  { propertyId, baseApiUrl, shouldDisableInjectedFields }
) {
  if (!hostElement || !propertyId) return () => {}

  const ctx = {
    hostElement,
    providersUrl: `${baseApiUrl}/api/impowerX/properties/${propertyId}/preferred-providers`,
    shouldDisableInjectedFields,
    providerTypes: [],
    saved: [],
    state: [],
    loaded: false,
    isSaving: false,
    disposed: false,
    searchTimer: null,
    searchSeq: 0,
    contactNames: new Map(),
  }

  const onClick = (event) => luxPpOnClick(ctx, event)
  const onInput = (event) => luxPpOnInput(ctx, event)
  const onFocusOut = (event) => luxPpOnFocusOut(ctx, event)

  hostElement.addEventListener('click', onClick)
  hostElement.addEventListener('input', onInput)
  hostElement.addEventListener('focusout', onFocusOut)
  luxPpInjectStyles()

  let lastDisabled = luxPpIsDisabled(ctx)
  const modeTimer = setInterval(() => {
    if (!ctx.loaded) return
    const disabled = luxPpIsDisabled(ctx)
    if (disabled === lastDisabled) return
    lastDisabled = disabled
    if (disabled) ctx.state = [...ctx.saved]
    luxPpRender(ctx)
  }, 500)

  luxPpLoadInitial(ctx)

  return () => {
    ctx.disposed = true
    clearTimeout(ctx.searchTimer)
    clearInterval(modeTimer)
    hostElement.removeEventListener('click', onClick)
    hostElement.removeEventListener('input', onInput)
    hostElement.removeEventListener('focusout', onFocusOut)
    // Clear contents but leave the host marker so the page observer does not
    // immediately reinject while this teardown is part of a rematch. Page leave
    // removes the host with the rest of the PropertyCare DOM.
    hostElement.innerHTML = ''
    delete hostElement.dataset.luxPpLoad
  }
}

// Referenced from onPropertyPageMatch.js after gulp concat (hoisted declaration).
void initPreferredProvidersPropertySection
